import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import api from '../services/api';
import StorageBar from '../components/StorageBar';
import './SettingsPage.css';

export default function SettingsPage() {
  const { user } = useAuth();
  const [watermarkText, setWatermarkText] = useState('');
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    api.get('/api/auth/me').then(({ data }) => {
      setWatermarkText(data.user.watermarkText || '');
    }).catch(() => {});
  }, []);

  async function handleSave(e) {
    e.preventDefault();
    setSaving(true);
    setSaved(false);
    setError('');
    try {
      const { data } = await api.patch('/api/auth/settings', {
        watermarkText: watermarkText.trim() || null,
      });
      const stored = JSON.parse(localStorage.getItem('mv_user') || '{}');
      localStorage.setItem('mv_user', JSON.stringify({ ...stored, watermarkText: data.user.watermarkText }));
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to save settings');
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="settings-page page-wrapper">
      <div className="settings-header">
        <h1>Settings</h1>
      </div>

      <div className="settings-sections">

        {/* ── Storage ── */}
        <section className="settings-section">
          <div className="settings-section-head">
            <h2>Storage</h2>
            <p>
              Your storage limit is set by your plan. Used storage is calculated
              from your original uploaded files. Contact your administrator to increase your limit.
            </p>
          </div>
          <StorageBar />
        </section>

        {/* ── Watermark ── */}
        <section className="settings-section">
          <div className="settings-section-head">
            <h2>Image watermark</h2>
            <p>
              When set, this text is tiled diagonally across every image shown in the viewer —
              both in your own gallery and in shared links. The original file is always
              watermark-free when downloaded via the Download button.
            </p>
          </div>

          {error && <div className="banner banner-error">{error}</div>}

          <form onSubmit={handleSave} className="settings-form">
            <label className="settings-label">
              Watermark text
              <input
                type="text"
                value={watermarkText}
                onChange={(e) => setWatermarkText(e.target.value)}
                placeholder="e.g. © Your Studio Name  •  Confidential"
                maxLength={80}
              />
              <span className="settings-hint">Leave empty to disable watermarks.</span>
            </label>

            {watermarkText.trim() && (
              <div className="settings-preview">
                <span className="settings-preview-label">Preview</span>
                <WatermarkPreview text={watermarkText} />
              </div>
            )}

            <div className="settings-actions">
              <button type="submit" className="btn btn-primary" disabled={saving}>
                {saving ? <span className="spinner" /> : 'Save settings'}
              </button>
              {saved && <span className="settings-saved">✓ Saved</span>}
            </div>
          </form>
        </section>

        {/* ── Account ── */}
        <section className="settings-section">
          <div className="settings-section-head">
            <h2>Account</h2>
          </div>
          <div className="settings-info-row">
            <span className="settings-info-label">Name</span>
            <span>{user?.name}</span>
          </div>
          <div className="settings-info-row">
            <span className="settings-info-label">Email</span>
            <span>{user?.email}</span>
          </div>
        </section>

      </div>
    </div>
  );
}

function WatermarkPreview({ text }) {
  const canvasRef = React.useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const w = canvas.width  = canvas.offsetWidth  || 320;
    const h = canvas.height = canvas.offsetHeight || 160;
    const ctx = canvas.getContext('2d');

    ctx.fillStyle = '#1a1a1a';
    ctx.fillRect(0, 0, w, h);

    const fontSize = Math.max(11, Math.round(Math.min(w, h) * 0.07));
    ctx.font = `600 ${fontSize}px Arial, sans-serif`;
    ctx.fillStyle = 'rgba(255,255,255,0.22)';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';

    const tileW = Math.round(fontSize * (text.length * 0.65 + 4));
    const tileH = Math.round(fontSize * 3.5);
    const cols = Math.ceil(w / tileW) + 2;
    const rows = Math.ceil(h / tileH) + 2;

    for (let r = -1; r < rows; r++) {
      for (let c = -1; c < cols; c++) {
        const x = c * tileW + (r % 2 === 0 ? 0 : tileW / 2);
        const y = r * tileH + tileH / 2;
        ctx.save();
        ctx.translate(x, y);
        ctx.rotate(-35 * Math.PI / 180);
        ctx.fillText(text, 0, 0);
        ctx.restore();
      }
    }
  }, [text]);

  return <canvas ref={canvasRef} className="wm-preview-canvas" />;
}
